const guides = [
  {
    slug: "getting-started",
    order: 2,
    title: "Getting Started with SnapConX",
    description: "Find businesses, explore services and manage appointments as a guest or customer.",
    file: "SnapConX_User_Guide.pdf",
    audience: "Guests and customers",
    steps: [
      ["Start as a guest", "Open SnapConX and choose to continue as a Guest. You can browse businesses and services before creating an account."],
      ["Find services near you", "Use Current Location and allow location access when prompted. SnapConX can show businesses within a 15 km radius."],
      ["Search another area", "Select Search Address, enter an area, suburb, town or address, and choose the right result."],
      ["Browse categories", "Use the categories on the Home screen to explore service types. Select the active category again to clear the filter."],
      ["Review businesses", "Select a business result to see its profile, services, prices, availability and gallery."],
      ["Save or book", "Sign in or register when you want to save a favourite or make an appointment. Choose a service, date and available time to begin booking."],
      ["Search directly", "Use the Search businesses field when you already know the business or service you need."],
    ],
    tips: ["Browsing is available before you create an account.", "Saving favourites and completing bookings requires signing in.", "Check the business profile before you choose a time slot."],
  },
  {
    slug: "installation",
    order: 1,
    title: "Installation Guide",
    description: "Install SnapConX on an Android device and complete the initial setup.",
    file: "SnapConX_Installation_Guide.pdf",
    audience: "New Android users",
    steps: [
      ["Open Google Play", "From the Android home screen, open the Google Play Store."],
      ["Search for SnapConX", "Tap the search bar, enter SnapConX and run the search."],
      ["Open the app listing", "Choose SnapConX from the search results to open its details page."],
      ["Install", "Tap Install and wait for the download and installation to complete. Keep the Play Store open while it installs."],
      ["Open SnapConX", "Open the app once installation is complete. It may show a brief loading screen while business information is retrieved."],
      ["Allow location access", "When prompted, select Allow only while using the app so SnapConX can use your device location."],
      ["Start browsing", "Once the Home screen appears, the app is ready to use. You can browse businesses as a guest."],
    ],
    tips: ["A location permission helps SnapConX show businesses close to you.", "Wait for the loading screen to finish before using the app."],
  },
  {
    slug: "search-and-book",
    order: 4,
    title: "Search and Book Services and Order Products",
    description: "Find a provider, select a service and send a booking request.",
    file: "SnapConX_Search_and_Book_Services_Guide.pdf",
    audience: "Signed-in customers",
    steps: [
      ["Open Home", "Use the Home screen to search by business or service, location, address or category."],
      ["Search and select a business", "Enter what you need in the search bar, then select a matching business card to open its profile."],
      ["Review services and products", "Check the service name, description, price, duration and days it is available."],
      ["Choose services and products", "In Book an Appointment, select the service you want. The selected service shows its price and duration."],
      ["Choose a date", "Select a date or use the Quick Select choices for an upcoming date."],
      ["Choose a time", "Select one of the time slots shown as available for that date."],
      ["Review and send", "Confirm the service, date, time and total amount. Tap Send Booking Request, then Send Request in the confirmation dialog."],
      ["Track updates", "Use Messages to communicate with the business and Profile/Appointments to check appointment information."],
    ],
    tips: ["Only choose time slots shown as available.", "Check the date, time, price and duration before sending a request.", "Keep an eye on Messages for booking and payment updates."],
  },
  {
    slug: "signed-in-browsing",
    order: 3,
    title: "Browse While Signed In",
    description: "Use the signed-in Home screen, profile controls and account options.",
    file: "SnapConX_Signed_In_Browse_Guide.pdf",
    audience: "Signed-in users",
    steps: [
      ["Explore the Home screen", "Search for businesses and services, use location controls, browse promotional offers, categories and nearby businesses."],
      ["Use the bottom navigation", "Move between Home, Create, Dashboard, Messages and Profile from the bottom navigation bar."],
      ["Open your profile", "Tap the profile icon to access your profile, booking history, privacy information, settings, help, terms and sign out."],
      ["View account information", "Open Profile to see saved information such as your email address and phone number."],
      ["Edit your profile", "Use Edit Profile to update your full name, email, phone number, bio and profile image, then select Save Changes."],
      ["Check appointments", "From Profile, select Appointments to view scheduled appointments. A message is shown if there are no upcoming appointments."],
      ["Manage account options", "Profile also provides Logout and Delete Account. Delete Account shows a confirmation before it permanently removes associated data."],
    ],
    tips: ["Use Messages for conversations with businesses.", "Choose Cancel in the account deletion confirmation if you do not want to continue."],
  },
  {
    slug: "create-business-profile",
    order: 5,
    title: "Create a Business Profile",
    description: "Set up a complete business profile, upload documents and submit it for verification.",
    file: "SnapConX_Create_Business_Profile_Guide.pdf",
    audience: "Business owners",
    steps: [
      ["Prepare your details", "Have your business name, description, address, contact details, opening hours, photos and verification PDFs ready."],
      ["Open Create", "From the bottom navigation, select Create to open the Create Business Profile form."],
      ["Add images", "Add a clear main business photo. You may also add up to four supporting images of the business, premises, work or services."],
      ["Add business information", "Enter the business name, description, address, phone number, email address and website where applicable."],
      ["Set operating hours", "Set each day to Open or Closed, and enter the correct start and end time for open days."],
      ["Choose a category", "Select the category that best represents the business so customers can find it more easily."],
      ["Upload documents", "Under Company Documents, upload the required verification PDFs, such as a registration certificate, CIPC document or ID."],
      ["Review and create", "Check all required fields, photos, hours, category and documents. Then tap Create Profile to submit it for processing."],
      ["Manage after creation", "Use My Businesses to open and manage the new profile, services and availability. The business becomes available after verification by the SnapConX team."],
    ],
    tips: ["Fields marked with an asterisk are required.", "Use accurate contact details and actual operating hours.", "If a document does not upload, confirm that it is a PDF and try again."],
  },
  {
    slug: "manage-businesses-and-services",
    order: 6,
    title: "Manage Businesses and Add Services and Products",
    description: "Update a business profile, manage archived listings and create service availability.",
    file: "SnapConX_Manage_Businesses_Create_Services_Guide.pdf",
    audience: "Business owners",
    steps: [
      ["Open Manage Businesses", "From the signed-in app, open Dashboard to view the business profiles associated with your account."],
      ["Select a business", "On My Businesses, select the business profile you want to manage."],
      ["Archive when needed", "Use the archive option to remove a business from the active list, then confirm the action in the Archive Business dialog."],
      ["Review archived businesses", "Open Archived Businesses to view profiles that were previously archived."],
      ["Edit the profile", "Use Edit Business to update the profile image and business information."],
      ["Review existing services", "Under Services & Availability, review services, durations, availability and the available edit or delete controls."],
      ["Add a service", "Choose Add New Service and enter its name, price, duration and an optional description."],
      ["Set availability", "Choose the service days and set start and end times. Use Add Time Slot when a day needs another availability period."],
      ["Save and confirm", "Select Add Service and confirm that it appears in the business profile's service list. Use Edit or Delete for future changes."],
    ],
    tips: ["Use Gallery Images to showcase the business, work or services.", "Review availability before saving so customers only see suitable time slots."],
  },
];

export default guides;
